import { useMemo, useState } from 'react';
import { attachmentUrl } from '../../api/client';
import type { Folder, Message } from '../../api/types';
import { addressLine, attachmentsOf, displayName, isFlagged } from '../../api/types';
import { folderLabel } from '../../format';
import { fullDate, fileSize } from '../../format';
import { sanitizeEmailHtml, escapePlain } from '../../mail/sanitize';
import { Button, Select } from '../../ui';

interface Props {
	message: Message | null;
	loading: boolean;
	error: string;
	folders: Folder[];
	archiveFolder: string | null;
	junkFolder: string | null;
	onBack: () => void;
	onReply: (message: Message, all: boolean) => void;
	onForward: (message: Message) => void;
	onDelete: (message: Message) => void;
	onFlag: (message: Message, flagged: boolean) => void;
	onUnread: (message: Message) => void;
	onMove: (message: Message, folder: string) => void;
}

export function MessageView({
	message,
	loading,
	error,
	folders,
	archiveFolder,
	junkFolder,
	onBack,
	onReply,
	onForward,
	onDelete,
	onFlag,
	onUnread,
	onMove
}: Props) {
	const [allowRemoteImages, setAllowRemoteImages] = useState(false);

	const attachments = useMemo(() => (message ? attachmentsOf(message) : []), [message]);

	const body = useMemo(() => {
		if (!message) {
			return null;
		}
		if (message.html) {
			return sanitizeEmailHtml(message.html, attachments, { allowRemoteImages });
		}
		return null;
	}, [message, attachments, allowRemoteImages]);

	// Every message starts with images blocked again; carrying the choice across
	// messages would silently opt someone into loading trackers from a sender
	// they never made that decision for.
	const key = message ? `${message.folder}:${message.uid}` : '';

	if (loading) {
		return (
			<section className="pane reading">
				<header className="pane__header">
					<Button variant="ghost" size="sm" className="pane__back" onClick={onBack}>← Back</Button>
				</header>
				<p className="reading__empty">Loading…</p>
			</section>
		);
	}

	if (error) {
		return (
			<section className="pane reading">
				<header className="pane__header">
					<Button variant="ghost" size="sm" className="pane__back" onClick={onBack}>← Back</Button>
				</header>
				<p className="reading__empty ks-error">{error}</p>
			</section>
		);
	}

	if (!message) {
		return (
			<section className="pane reading">
				<p className="reading__empty">Select a message to read it.</p>
			</section>
		);
	}

	return (
		<section className="pane reading" key={key}>
			{/*
			 * Every action is written out. Icon-only toolbars were the single most
			 * common "what does this do" complaint about the old UI. Reply and
			 * forward live under the message instead, where you are when you have
			 * finished reading it -- Gmail's layout.
			 */}
			<header className="pane__header reading__toolbar">
				<Button variant="ghost" size="sm" className="pane__back" onClick={onBack}>← Back</Button>
				<div className="reading__actions">
					{archiveFolder && archiveFolder !== message.folder && (
						<Button variant="ghost" size="sm" onClick={() => onMove(message, archiveFolder)} title="Archive (e)">
							⬒ Archive
						</Button>
					)}
					<Button variant="ghost" size="sm" onClick={() => onDelete(message)} title="Delete (#)">
						⌫ Delete
					</Button>
					{junkFolder && junkFolder !== message.folder && (
						<Button variant="ghost" size="sm" onClick={() => onMove(message, junkFolder)} title="Report spam">
							⚠ Spam
						</Button>
					)}
					<Button variant="ghost" size="sm" onClick={() => onUnread(message)} title="Mark unread (u)">
						✉ Mark unread
					</Button>
					<Select
						size="sm"
						value=""
						aria-label="Move to folder"
						onChange={event => {
							if (event.target.value) {
								onMove(message, event.target.value);
							}
						}}
					>
						<option value="">Move to…</option>
						{folders
							.filter(item => item.fullName !== message.folder)
							.map(item => (
								<option key={item.fullName} value={item.fullName}>
									{folderLabel(item.fullName, item.delimiter)}
								</option>
							))}
					</Select>
					<Button
						variant="ghost"
						size="sm"
						title={isFlagged(message) ? 'Unstar (s)' : 'Star (s)'}
						aria-pressed={isFlagged(message)}
						onClick={() => onFlag(message, !isFlagged(message))}
					>
						{isFlagged(message) ? '★ Starred' : '☆ Star'}
					</Button>
				</div>
			</header>

			<div className="reading__body">
				<h1 className="reading__subject">{message.subject || '(no subject)'}</h1>

				<div className="reading__meta">
					<div>
						<div className="reading__from">
							{displayName(message.from)}
							{message.from?.[0]?.email && message.from[0].name && (
								<span className="ks-muted"> &lt;{message.from[0].email}&gt;</span>
							)}
						</div>
						<div>to {addressLine(message.to) || 'undisclosed recipients'}</div>
						{message.cc?.length ? <div>cc {addressLine(message.cc)}</div> : null}
					</div>
					<div>{fullDate(message.dateTimestamp)}</div>
				</div>

				{body?.blockedRemoteImages && (
					<div
						style={{
							marginTop: 16,
							padding: '10px 12px',
							background: 'var(--surface)',
							border: '1px solid var(--border)',
							borderRadius: 'var(--radius-control)',
							display: 'flex',
							alignItems: 'center',
							gap: 12,
							flexWrap: 'wrap'
						}}
					>
						<span className="ks-muted" style={{ fontSize: 13 }}>
							Images in this message were not loaded. They can tell the sender you opened it.
						</span>
						<Button variant="outline" size="sm" onClick={() => setAllowRemoteImages(true)}>
							Show images
						</Button>
					</div>
				)}

				{body ? (
					/*
					 * Sanitized by DOMPurify in mail/sanitize.ts. SnappyMail does NOT
					 * sanitize server-side -- its own cleaning is client-side -- so this
					 * must never be given `message.html` directly.
					 */
					<div
						className="reading__content"
						dangerouslySetInnerHTML={{ __html: body.html }}
					/>
				) : (
					<div
						className="reading__content reading__plain"
						dangerouslySetInnerHTML={{ __html: escapePlain(message.plain ?? '') }}
					/>
				)}

				{attachments.length > 0 && (
					<div className="attachments">
						{attachments.map(attachment => (
							<a
								key={attachment.mimeIndex}
								className="attachment"
								href={attachmentUrl(attachment, 'Download')}
								download={attachment.fileName}
							>
								<span>{attachment.fileName || 'attachment'}</span>
								<span className="attachment__size">{fileSize(attachment.estimatedSize)}</span>
							</a>
						))}
					</div>
				)}

				<div className="reading__reply">
					<Button variant="outline" onClick={() => onReply(message, false)} title="Reply (r)">
						↩ Reply
					</Button>
					{hasOthers(message) && (
						<Button variant="outline" onClick={() => onReply(message, true)} title="Reply all (a)">
							↩↩ Reply all
						</Button>
					)}
					<Button variant="outline" onClick={() => onForward(message)} title="Forward (f)">
						↪ Forward
					</Button>
				</div>
			</div>
		</section>
	);
}

/** Reply all only means something when someone besides the sender got it. */
function hasOthers(message: Message): boolean {
	return (message.to?.length ?? 0) + (message.cc?.length ?? 0) > 1;
}
